import { Css, FormSectionLayout, useComputed, useTestIds } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ProjectParams } from "src/routes/routesDef";
import { fail, formatCentsToPrice, queryResult } from "src/utils";
import { useReviewConfigurationStepQuery } from "./2.ReviewConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { ReviewConfigurationGroup, toReviewConfigurationSections } from "./reviewConfigurationUtils";

type ReviewConfigurationStepProps = { formState: ObjectState<ApplyConfigurationFormInput> };

/**
 * A completely read-only breakdown of the configuration picked on step 1, so the user can confirm it before Review Impact.
 */
export function ReviewConfigurationStep(props: ReviewConfigurationStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const checkoutConfigId = useComputed(() => formState.checkoutConfigId.value, [formState]);
  const query = useReviewConfigurationStepQuery({
    variables: { structureId: projectId, configId: checkoutConfigId ?? fail("No checkout config ID") },
  });
  return queryResult(query, ({ structure, checkoutConfig }) => (
    <FormSectionLayout
      title="Review New Configuration"
      description={"Review your new configuration below."}
      withJumpLinks
      sections={toReviewConfigurationSections(structure, checkoutConfig).map((section) => ({
        title: section.title,
        fields: <ConfigReviewSection groups={section.groups} />,
      }))}
    />
  ));
}

type ConfigReviewSectionProps = { groups: ReviewConfigurationGroup[] };
function ConfigReviewSection(props: ConfigReviewSectionProps) {
  const { groups } = props;
  const tid = useTestIds({}, "configReviewGroup");
  return (
    <div css={Css.df.fdc.$}>
      {groups.map((group) => (
        <div css={Css.df.fdc.gap1.py2.bb.bcGray200.$} {...tid} key={group.title}>
          <div css={Css.df.aic.jcsb.gap2.$}>
            <span css={Css.mdSb.gray900.$} {...tid.title}>
              {group.title}
            </span>
            <span css={Css.mdSb.gray900.wsnw.$} {...tid.amount}>
              {formatGroupAmount(group.amountInCents)}
            </span>
          </div>
          {group.items.map((item) => (
            <div key={item.title} css={Css.df.aic.jcsb.gap2.$}>
              <span css={Css.sm.gray900.$} {...tid.itemTitle}>
                {item.title}
              </span>
              {item.amountInCents !== undefined && (
                <span css={Css.sm.gray900.wsnw.$} {...tid.itemAmount}>
                  {formatItemAmount(item.amountInCents)}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function formatGroupAmount(amountInCents: number): string {
  return amountInCents === 0 ? "$0" : formatCentsToPrice(amountInCents, { trim: true });
}

function formatItemAmount(amountInCents: number): string {
  // A selection that costs nothing reads better as "Included" than as "$0"
  return amountInCents === 0 ? "Included" : formatCentsToPrice(amountInCents, { trim: true });
}
